/**
 * The page a suspended tenant's users see instead of the app (phase 7b): the
 * cloud answers every request `suspended` (HTTP 423) with the vendor's
 * reason, and this says so plainly, with the reason, what still works (the
 * studio's nodes keep running their productions) and a way to sign out.
 */
import { LockIcon, LogOutIcon } from 'lucide-react';
import { Button } from './ui/button.js';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card.js';

export type TenantSuspendedProps = {
  /** The server's message: the vendor's reason. */
  readonly message: string;
  readonly tenantId: string | null;
  readonly onSignOut: () => void;
};

export function TenantSuspended({ message, tenantId, onSignOut }: TenantSuspendedProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground" data-testid="tenant-suspended">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LockIcon className="size-5 text-destructive" aria-hidden="true" />
            This account is suspended
          </CardTitle>
          <CardDescription>{tenantId === null ? 'Your organisation’s ACP account' : `The ACP account of ${tenantId}`} has been suspended by the vendor.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm">
          <p role="alert" className="rounded-md border border-destructive px-3 py-2" data-testid="suspended-message">
            {message}
          </p>
          <p className="text-muted-foreground">Productions already running on your studio servers keep running locally. Contact your vendor to restore access.</p>
        </CardContent>
        <CardFooter>
          <Button variant="outline" onClick={onSignOut} data-testid="suspended-sign-out">
            <LogOutIcon />
            Sign out
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
